import { useBinnedDataBufferCache } from 'features/dataLayers/load/useBinnedDataBufferCache'
import { RolodexCacheInstance } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useGetUniqueNumberGenerator } from 'utils/useGetUniqueNumberGenerator'

import { createCdf, CreateCdfParams, RawCdfEntry } from './cdfGenerator'

/**
 * A hook that returns both a function that can be used to create a custom CDF
 * and a function that can be used to tear down (remove all references to) a custom CDF.
 *
 * When this hook is unmounted, all custom CDFs created by it will automatically be removed from the cache.
 */
export function useGetCustomCdfUpsertDeleteFns(
  rolodexCacheInstance: RolodexCacheInstance
): {
  createCdfFn: (cdfID: string, args: CreateCdfParams) => RawCdfEntry
  tearDownCdfFn: (cdfID: string) => void
} {
  /**
   * Data Organization
   */

  const { putEntry, resetEntry } = rolodexCacheInstance
  const { putBinnedDataBufferInCache, deleteCachedBinnedDataBuffer } =
    useBinnedDataBufferCache()

  const allCdfsGenerated = useRef<RawCdfEntry[]>([])

  /**
   * Data Upsert
   */

  const uniqueNumberGenerator = useGetUniqueNumberGenerator()
  const createCdfFn = useCallback(
    (cdfID: string, args: CreateCdfParams) => {
      const generationNumberToUse = args.generation ?? uniqueNumberGenerator()

      const cdfData = createCdf(cdfID, {
        ...args,
        generation: generationNumberToUse,
      })

      allCdfsGenerated.current = [
        cdfData.entry,
        ...allCdfsGenerated.current.filter(({ id }) => id !== cdfData.entry.id),
      ]
      putEntry(cdfData.entry)
      putBinnedDataBufferInCache({
        id: cdfData.entry.id,
        generation: cdfData.entry.generation,
        data: cdfData.getBinnedDataBufferFn,
      })
      return cdfData.entry
    },
    [putBinnedDataBufferInCache, putEntry, uniqueNumberGenerator]
  )

  /**
   * Data Teardown
   */

  const tearDownCdfFn = useCallback(
    (cdfID: string) => {
      resetEntry(cdfID)
      deleteCachedBinnedDataBuffer(cdfID)
    },
    [resetEntry, deleteCachedBinnedDataBuffer]
  )

  useEffect(() => {
    return () => {
      allCdfsGenerated.current.forEach(({ id }) => tearDownCdfFn(id))
    }
  }, [deleteCachedBinnedDataBuffer, resetEntry, tearDownCdfFn])

  /**
   * Fn Return
   */

  return useMemo(
    () => ({
      createCdfFn,
      tearDownCdfFn,
    }),
    [createCdfFn, tearDownCdfFn]
  )
}
